import { cva, type VariantProps } from "class-variance-authority"

import { twMerge } from "tailwind-merge"

const button = cva(
    [
        "shadow-sm",
        "group",
        "inline-flex",
        "items-center",
        "justify-center",
        "rounded-md",
        "font-semibold",
        "text-center",
        "focus:outline",
        "focus-visible:outline-2",
        "focus-visible:outline-offset-2",
    ],
    {
        variants: {
            intent: {
                primary: [
                    "bg-primary-600",
                    "text-white",
                    "hover:bg-primary-700",
                    "cursor-pointer",
                    "focus-visible:bg-primary-800",
                    "disabled:bg-gray-300",
                    "disabled:text-gray-400",
                    "disabled:cursor-not-allowed",
                ],
                secondary: [
                    "bg-transparent",
                    "ring-slate-200",
                    "text-slate-700",
                    "ring-1",
                    "hover:enabled:text-white",
                    "hover:enabled:text-slate-900",
                    "hover:enabled:ring-slate-300",
                    "disabled:bg-gray-100",
                    "disabled:text-gray-400",
                    "disabled:cursor-not-allowed",
                ],
            },
            size: {
                sm: ["min-w-10", "h-full", "min-h-10", "text-sm", "py-1.5", "px-2.5"],
                md: ["min-w-12", "h-full", "min-h-12", "text-sm", "py-2", "px-4"],
                lg: ["min-w-16", "h-full", "min-h-12", "text-sm", "py-2.5", "px-5"],
            },
            underline: { true: ["underline"], false: [] },
        },
        defaultVariants: {
            intent: "primary",
            size: "md",
        },
    }
)

interface BaseProps extends VariantProps<typeof button> {
    type?: "button" | "submit"
    className?: string
    underline?: boolean
    children: React.ReactNode
    disabled?: boolean
}

interface SubmitProps extends BaseProps {
    type: "submit"
    onClick?: (e: { preventDefault: () => void }) => void
    href?: never
}

interface LinkProps extends BaseProps {
    type?: never
    href: string
    onClick?: never
}

interface StandardButtonProps extends BaseProps {
    type?: "button"
    href?: never
    onClick?: (e: { preventDefault: () => void }) => void
}

type ButtonProps = SubmitProps | LinkProps | StandardButtonProps

// export interface ButtonProps extends VariantProps<typeof button> {
//     className?: string
//     underline?: boolean
//     href?: string
//     onClick?: (e: { preventDefault: () => void }) => void
//     children: React.ReactNode
//     disabled?: boolean
// }

export function Button({ className, intent, size, underline, href, ...props }: ButtonProps) {
    return href ? (
        <a className={twMerge(button({ intent, size, className, underline }))} href={href} {...props}>
            {props.children}
        </a>
    ) : (
        <button className={twMerge(button({ intent, size, className, underline }))} {...props}>
            {props.children}
        </button>
    )
}
